import React, { useEffect, useState } from 'react';

import { LngLat, LngLatBounds } from 'mapbox-gl';

import { DEFAULT_HEADERS } from '../../api/rest/shared';
import {
  DEFAULT_MAP_STYLE,
  MapStyle,
  MAP_STYLE_URLS,
  RUTILUS_PRODUCTION_DOMAIN,
  RUTILUS_STAGING_DOMAIN,
  API_GATEWAY_STAGING_DOMAIN,
  API_GATEWAY_PRODUCTION_DOMAIN,
  MAP_MAXBOUNDS_SOUTH,
  MAP_MAXBOUNDS_WEST,
  MAP_MAXBOUNDS_NORTH,
  MAP_MAXBOUNDS_EAST,
} from '../../components/Maps/constants/constants';
import {
  addMapboxLayers,
  filterCatchPositions,
  filterLayersBasedOnToggleableGroups,
  filterWater,
  orderMapboxLayers,
  removeMapboxLayers,
} from '../../components/Maps/mapLoading/editLayers';
import { appendSources, removeSources } from '../../components/Maps/mapLoading/editSources';
import { CatchFilters, FishbrainLayer } from '../../components/Maps/types/types';
import { WEB_GL_DISABLED_ERROR_MESSAGE } from '../../constants/constants';
import { UNIVERSAL_ENV_VARS } from '../../constants/environmentVariables';
import { useMap } from '../../contexts/MapContext';
import { useMapLayers } from '../../contexts/MapLayersContext';
import { ICoordinates } from '../../interfaces/geo';
import { _getAccessToken } from '../authentication/shared';
import { reportMykissError } from '../errorHandling';
import { useWindowSize } from '../hooks';
import { logger } from '../logging';
import { shallowRemoveBlankValues } from '../objects/shallowRemoveBlankValues';
import { getSecondsSinceEpoch } from '../time';
import { createUrlWithQuery } from '../url/queryUtils';

import { SMALL } from './useScreenWidth';
import { useUserInfo } from './useUserInfo';

type Mapbox = typeof import('mapbox-gl');

export enum MapboxExpectedError {
  WebGLDisabled = 0,
  Other = 1,
}

const MapOptionsDefaults: IMapOptions = {
  waterId: undefined,
  showGeolocateControl: true,
  navControlPosition: 'bottom-right',
  disableDragPan: false,
  fibLayers: ['fishingWaters'],
  catchFilters: { months: [], speciesIds: [] },
  mapStyle: DEFAULT_MAP_STYLE,
  minZoom: 0,
  maxZoom: 22,
  cooperativeGestures: false,
  logoPosition: 'bottom-left',
};

interface IMapOptions {
  waterId: string | undefined;
  showGeolocateControl: boolean;
  navControlPosition: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  disableDragPan: boolean;
  fibLayers: FishbrainLayer[];
  catchFilters: CatchFilters;
  mapStyle: MapStyle;
  minZoom?: number;
  maxZoom?: number;
  cooperativeGestures: boolean;
  logoPosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

interface IProps {
  error?: MapboxExpectedError;
}

export function useNewMap(
  mapbox: Mapbox | null, // The mapbox library, passed as an argument as it will be loaded only on the client
  container: React.RefObject<HTMLDivElement | null>,
  zoom: number,
  coordinates: ICoordinates,
  options?: Partial<IMapOptions>,
): IProps {
  const [error, setError] = useState<MapboxExpectedError>();
  const size = useWindowSize();
  const { isPremium, isLoadingUser } = useUserInfo();
  const { map, setMap, setMapLayersLoaded, isMapPreloaded, setIsMapPreloaded } = useMap();
  const { layers: toggleableLayers } = useMapLayers();

  const {
    fibLayers,
    waterId,
    navControlPosition,
    showGeolocateControl,
    disableDragPan,
    catchFilters,
    mapStyle,
    minZoom,
    maxZoom,
    logoPosition,
    cooperativeGestures,
  } = {
    ...MapOptionsDefaults,
    ...shallowRemoveBlankValues(options ?? {}),
  };

  const layersAsString = fibLayers.toString();

  // handles loading and changing Fishbrain layers
  useEffect(() => {
    if (isMapPreloaded && map && !isLoadingUser) {
      setMapLayersLoaded(false);

      removeMapboxLayers(map, fibLayers);
      removeSources(map, fibLayers);
      appendSources(map, isPremium, fibLayers);
      addMapboxLayers(map, fibLayers);
      orderMapboxLayers(map, fibLayers);
      filterCatchPositions(map, catchFilters);

      filterLayersBasedOnToggleableGroups({
        map,
        layers: toggleableLayers,
      });

      if (waterId) {
        filterWater(map, waterId);
      }

      setMapLayersLoaded(true);
    }

    return () => {
      setMapLayersLoaded(false);
    };
    // We convert layers to a string so we don't get stuck in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layersAsString,
    isMapPreloaded,
    isPremium,
    isLoadingUser,
    map,
    setMapLayersLoaded,
    waterId,
    toggleableLayers,
    catchFilters,
  ]);

  useEffect(() => {
    if (!mapbox) {
      return () => {};
    }

    mapbox.accessToken = UNIVERSAL_ENV_VARS.MAPBOX_TOKEN;

    const maxBounds = new LngLatBounds(
      new LngLat(MAP_MAXBOUNDS_SOUTH, MAP_MAXBOUNDS_WEST),
      new LngLat(MAP_MAXBOUNDS_NORTH, MAP_MAXBOUNDS_EAST),
    );

    if (container && container.current) {
      try {
        logger.debug('Instantiating map component');
        const { latitude, longitude } = coordinates;
        logger.debug('Creating new map instance (i.e. we pay for this)');
        const newMap = new mapbox.Map({
          zoom,
          center: [longitude, latitude],
          container: container.current,
          style: MAP_STYLE_URLS[mapStyle],
          attributionControl: false,
          transformRequest: url => {
            if (
              url.startsWith(`${RUTILUS_STAGING_DOMAIN}/tiles/waypoints`) ||
              url.startsWith(`${RUTILUS_PRODUCTION_DOMAIN}/tiles/waypoints`)
            ) {
              return {
                url,
                // TODO: Expand this to cover use cases where the token expires during a user session (_get returns '')
                headers: {
                  Authorization: `Bearer ${_getAccessToken()}`,
                  timestamp: getSecondsSinceEpoch().toString(),
                  ...DEFAULT_HEADERS,
                },
              };
            }

            if (
              url.startsWith(`${API_GATEWAY_STAGING_DOMAIN}/tiles/catches`) ||
              url.startsWith(`${API_GATEWAY_PRODUCTION_DOMAIN}/tiles/catches`)
            ) {
              return {
                url: createUrlWithQuery({
                  url,
                  query: { authorization: `Bearer ${_getAccessToken()}` },
                }),
              };
            }

            return { url };
          },
          maxBounds,
          minZoom,
          maxZoom,
          cooperativeGestures,
          logoPosition,
        });

        newMap.addControl(
          new mapbox.AttributionControl({
            compact: true,
          }),
        );

        newMap.addControl(new mapbox.NavigationControl({ showCompass: false }), navControlPosition);

        if (showGeolocateControl) {
          if (size.width && size.width > SMALL) {
            newMap.addControl(
              new mapbox.GeolocateControl({
                positionOptions: {
                  enableHighAccuracy: true,
                },
                trackUserLocation: true,
              }),
              'bottom-right',
            );
          } else {
            newMap.addControl(
              new mapbox.GeolocateControl({
                positionOptions: {
                  enableHighAccuracy: true,
                },
                trackUserLocation: true,
              }),
              'bottom-left',
            );
          }
        }

        newMap.once('load', () => {
          setIsMapPreloaded(true);
        });

        if (disableDragPan) {
          newMap.dragPan.disable();
        } else {
          newMap.dragPan.enable();
        }

        // disable map rotation using right click + drag
        newMap.dragRotate.disable();

        // disable map rotation using touch rotation gesture
        newMap.touchZoomRotate.disableRotation();
        setMap(newMap);
        window.exploreMap = newMap;

        return () => {
          window.exploreMap = undefined;
          newMap.remove();
        };
      } catch (e) {
        if (e.message.includes(WEB_GL_DISABLED_ERROR_MESSAGE)) {
          setError(MapboxExpectedError.WebGLDisabled);
        } else {
          reportMykissError(e);
        }
        return () => {
          window.exploreMap = undefined;
        };
      }
    }
    return () => {
      setIsMapPreloaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container]);
  return { error };
}
